import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Stack, Select, MenuItem } from '@mui/material';
import { Icon } from '@iconify/react';
import { getOgdTeamColorById } from '../../../../utils/constants/functions';
import NoDataFound from '../../../../components/NodataIndication/NoDataFound';
import Loader3D from '../../../../components/Loader/Loader3D';
import { ogdWings } from '../../../../utils/constants/GlobalStaticData';
import { useDispatch } from 'react-redux';
import { getDailyTeamRevenue } from '../../../../redux/features/poinTrackerSlice';
import { useSelector } from 'react-redux';
import { restrict2digitAfterDecimal } from '../../../../utils/helperFunction';

const DailyTeamRevenue = ({parentDate}) => {
  const previousDate = useRef(parentDate);
  const { dailyTeamRevenue } = useSelector((state) => state.pointTracker);
  const [limit, setLimit] = useState(5);
  const dispatch = useDispatch();
  
  useEffect(() => {
    if(Object.keys(dailyTeamRevenue?.responseData ||{}).length === 0 || previousDate.current !== parentDate){
      dispatch(getDailyTeamRevenue({ date:parentDate }));
      previousDate.current = parentDate;
    }
  }, [parentDate,dispatch,dailyTeamRevenue?.responseData]);

  const HEADERS = dailyTeamRevenue?.responseData?.headers || ogdWings;
  const loading = dailyTeamRevenue?.loading;
  const noDatafound = dailyTeamRevenue?.responseData?.data?.length == 0;
  const REVENUE_DATA = useMemo(() => {
    return dailyTeamRevenue?.responseData?.data?.slice(0, limit) || [];
  }, [dailyTeamRevenue?.responseData?.data, limit]);

  return (
    <Paper
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(90deg, #2563eb, #7c3aed)',
          color: '#fff',
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
            }}
          >
            <Icon icon="picon:rise" width={24} />
          </Box>

          <Box>
            <Typography fontWeight={600}>Daily Team Revenue</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Revenue breakdown by team and date
            </Typography>
          </Box>
        </Stack>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <Loader3D />
        </Box>
      ) : noDatafound ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', height: '40vh' }}>
          <NoDataFound />
        </Box>
      ) : (
        <>
          {/*TABLE SECTION */}
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: `${120 + HEADERS.length * 180}px` }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `120px repeat(${HEADERS.length}, 180px)`,
                  p: 2,
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ ml: 1, }}fontWeight={600}>
                  Date
                </Typography>

                {HEADERS.map((h, i) => {
                  const { color, bgColor } = getOgdTeamColorById(h.id);

                  return (
                    <Typography
                      key={i}
                      textAlign="center"
                      fontWeight={600}
                      sx={{
                        color,
                        backgroundColor: bgColor,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        display: 'inline-block',
                        mx: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h.value}
                    </Typography>
                  );
                })}
              </Box>

              {REVENUE_DATA.map((data, i) => {
                return (
                  <Box
                    key={i}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `120px repeat(${HEADERS.length}, 180px)`,
                      alignItems: 'center',
                      p: 2,
                      borderBottom: '1px solid #f1f5f9',
                      '&:hover': { backgroundColor: '#f9fafb' },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography sx={{ ml: 1 }} variant="body2" fontWeight={300}>
                        {data?.date}
                      </Typography>
                    </Stack>

                    {HEADERS.map((val, idx) => {
                      const rowData = restrict2digitAfterDecimal(data?.revenue[val.id]) || 0;
                      const { color = '#e5e7eb', bgColor = '#000' } = getOgdTeamColorById(val.id);
                      return (
                        <Box key={idx} textAlign="center">
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '8px',
                              fontSize: '13px',
                              backgroundColor: bgColor,
                              color: color,
                            }}
                          >
                            &#8377; {rowData}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Footer Section */}
          <Box p={1}>
            {/* DROP DOWN SECTION */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Select
                size="small"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                sx={{
                  ml: 1,
                  height: 32,
                  fontSize: 12,
                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>

              <Typography variant="caption" fontSize={12}>
                {`${limit} / ${dailyTeamRevenue?.responseData?.data?.length}`}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default React.memo(DailyTeamRevenue);
